import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";

export interface DataColumn<T> {
  label: string;
  render: (row: T) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: DataColumn<T>[];
  rows: T[];
  loading?: boolean;
  emptyText?: string;
  onRowClick?: (row: T) => void;
  rowKey: (row: T, index: number) => string;
}

const headCellSx = {
  bgcolor: "#F2F4F7",
  color: "#475467",
  fontSize: 12,
  fontWeight: 600,
  borderBottom: "none",
  whiteSpace: "nowrap",
};

// Table with skeleton rows while loading and an empty row, like ActivityTable.
function DataTable<T>({
  columns,
  rows,
  loading,
  emptyText = "Nothing found",
  onRowClick,
  rowKey,
}: DataTableProps<T>) {
  return (
    <TableContainer sx={{ border: "1px solid #E4E7EC", borderRadius: "8px" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.label} sx={headCellSx}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                {columns.map((col) => (
                  <TableCell key={col.label}>
                    <Skeleton />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <Typography fontSize={14} color="#667085">
                  {emptyText}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, i) => (
              <TableRow
                key={rowKey(row, i)}
                hover={!!onRowClick}
                onClick={() => onRowClick?.(row)}
                sx={{ cursor: onRowClick ? "pointer" : "default" }}
              >
                {columns.map((col) => (
                  <TableCell key={col.label} sx={{ fontSize: 13, color: "#344054" }}>
                    {col.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default DataTable;
